import { toast } from 'sonner'
import type { User } from 'oidc-client-ts'
import Toast from '@common/components/Toast'
import { userManager } from '@auth/oidc.config'
import { clearSigningIntent } from './clearSigningIntent'
import { storeSigningIntent } from './storeSigningIntent'

export const SIGNING_INTENT_READY_EVENT = 'gcis:signing-intent-ready'

/** Stable window name so the parent can refocus the same popup without opening another. */
export const ESIGN_POPUP_WINDOW_TARGET = 'gcis-esign-reauth'

export const ESIGN_POPUP_ACTIVE_EVENT = 'gcis:esign-popup-active'

/** How often to check the retained popup window for `closed === true`. */
export const ESIGN_POPUP_CLOSE_POLL_MS = 250

export type SigningIntentParams = {
  pathParams?: Record<string, string>
  queryParams?: Record<string, unknown>
  signer_name: string
  reason?: string
  [key: string]: unknown
}

export class EsignReauthCancelledError extends Error {
  constructor(message = 'Popup closed by user') {
    super(message)
    this.name = 'EsignReauthCancelledError'
  }
}

let esignPopupActive = false
const esignPopupActiveListeners = new Set<(active: boolean) => void>()
let popupClosePollId: ReturnType<typeof setInterval> | undefined

function setEsignPopupActive(active: boolean) {
  if (esignPopupActive === active) {
    return
  }

  esignPopupActive = active
  esignPopupActiveListeners.forEach((listener) => listener(active))
  window.dispatchEvent(new CustomEvent(ESIGN_POPUP_ACTIVE_EVENT, { detail: { active } }))
}

function clearPopupClosePoll() {
  if (popupClosePollId !== undefined) {
    clearInterval(popupClosePollId)
    popupClosePollId = undefined
  }
}

/**
 * Open the named e-sign window as a real popup (with features), matching
 * oidc-client-ts defaults. Calling window.open(url, name) without features
 * opens a browser tab; signinPopup would then reuse that tab.
 */
function openEsignPopupWindow(): Window | null {
  // Mirror oidc-client-ts DefaultPopupWindowFeatures + PopupUtils.center.
  const width = [800, 720, 600, 480].find((candidate) => candidate <= window.outerWidth / 1.618) ?? 360
  const height = 640
  const left = Math.max(0, Math.round(window.screenX + (window.outerWidth - width) / 2))
  const top = Math.max(0, Math.round(window.screenY + (window.outerHeight - height) / 2))
  const features = [
    `width=${width}`,
    `height=${height}`,
    `left=${left}`,
    `top=${top}`,
    'location=no',
    'toolbar=no',
  ].join(',')

  return window.open('', ESIGN_POPUP_WINDOW_TARGET, features)
}

/**
 * Watch a retained popup Window for close. Hides overlay UI immediately when
 * `closed === true` without resolving/rejecting the signinPopup promise.
 * `finally` still calls this cleanup as a safe final reset.
 */
function watchEsignPopupClosed(popup: Window) {
  clearPopupClosePoll()

  popupClosePollId = setInterval(() => {
    if (!popup.closed) {
      return
    }

    // Popup is gone — hide overlay now. Do not touch signinPopup.
    clearPopupClosePoll()
    setEsignPopupActive(false)
  }, ESIGN_POPUP_CLOSE_POLL_MS)
}

export function isEsignPopupActive(): boolean {
  return esignPopupActive
}

export function subscribeEsignPopupActive(listener: (active: boolean) => void): () => void {
  esignPopupActiveListeners.add(listener)
  return () => {
    esignPopupActiveListeners.delete(listener)
  }
}

/**
 * Bring the existing named e-sign popup to the front when possible.
 * Does not open a new auth popup. Returns false if inactive, closed, or focus failed.
 */
export function focusEsignPopup(): boolean {
  if (!esignPopupActive) {
    return false
  }

  try {
    const popup = window.open('', ESIGN_POPUP_WINDOW_TARGET)
    if (!popup) {
      return false
    }

    if (popup.closed) {
      return false
    }

    // window.open('', name) creates a blank window when the named popup is gone.
    // Close that accidental blank so we never leave a stray empty window.
    try {
      if (popup.location.href === 'about:blank') {
        popup.close()
        return false
      }
    } catch {
      // Cross-origin (Keycloak) — expected while re-auth is in progress.
    }

    popup.focus()
    return true
  } catch {
    return false
  }
}

export function notifySigningIntentReady() {
  window.dispatchEvent(new Event(SIGNING_INTENT_READY_EVENT))
}

export function isEsignPopupCancellation(error: unknown): boolean {
  if (error instanceof EsignReauthCancelledError) {
    return true
  }

  const message = error instanceof Error ? error.message : String(error ?? '')
  return /popup closed/i.test(message)
}

function getReauthErrorMessage(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error ?? '')

  if (/popup blocked/i.test(message)) {
    return 'Pop-up blocked. Please allow pop-ups for this site and try signing again.'
  }

  if (isEsignPopupCancellation(error)) {
    return 'E-sign cancelled. The submission was not completed.'
  }

  return 'E-sign could not be completed. The submission was not completed.'
}

/**
 * E-sign-only forced re-authentication via OIDC popup.
 * Uses UserManager.signinPopup directly (not react-oidc-context's wrapped
 * signinPopup) so popup cancel/close does not set auth.error and send the
 * app to ProtectedApp's "We've hit a snag" page.
 *
 * Signing intent is stored only after a successful popup re-auth so
 * useRestoreSigningProcess never submits with a pre-reauth token.
 *
 * Overlay "active" tracks the real popup window (via closed polling), not the
 * full signinPopup promise — so the parent overlay hides as soon as the window
 * closes, even while token processing is still finishing.
 */
export async function requestEsignReauthentication<TContext>(
  payload: TContext,
  params: SigningIntentParams,
): Promise<User> {
  // Retain a real popup (with features) before signinPopup so we can poll
  // `closed`. signinPopup reuses this named target and navigates it — it must
  // not be pre-opened without features, or the browser opens a tab instead.
  const popup = openEsignPopupWindow()

  if (popup && !popup.closed) {
    setEsignPopupActive(true)
    watchEsignPopupClosed(popup)
  }

  try {
    const user = await userManager.signinPopup({
      prompt: 'login',
      max_age: 0,
      redirect_uri: `${window.location.origin}${window.location.pathname}`,
      popupWindowTarget: ESIGN_POPUP_WINDOW_TARGET,
    })

    if (!user?.access_token) {
      throw new Error('Re-authentication did not return an access token')
    }

    storeSigningIntent(payload, params)
    notifySigningIntentReady()

    return user
  } catch (error) {
    clearSigningIntent()

    const normalizedError = isEsignPopupCancellation(error)
      ? new EsignReauthCancelledError(error instanceof Error ? error.message : undefined)
      : error

    toast(<Toast variant="error" message={getReauthErrorMessage(normalizedError)} />)
    throw normalizedError
  } finally {
    // Safe final reset if close-polling already hid the overlay, or if the
    // popup was blocked and never marked active.
    clearPopupClosePoll()
    setEsignPopupActive(false)
  }
}
